import { SidebarProvider } from '@/design-system/sidebar';
import * as Sentry from '@sentry/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useErrorBoundary } from 'react-error-boundary';
import { Outlet, useNavigate } from 'react-router';
import { useData } from '../../contexts/data-context/use-data';
import { useUserInfo } from '../../contexts/user-context/use-user-info';
import { LoadingSection } from '../../design-system/loading/loading';
import { canInitiateExternalPayments, canInitiateInternalPayments } from '../payments/utils/capability-checks';
import { SideNavItem } from './components/side-nav';
import { SideNavNew } from './components/side-nav-new';

const Layout = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, mutations } = useData();
  const { authenticatedInfo, mutations: userMutations } = useUserInfo();
  const { showBoundary } = useErrorBoundary();
  const { invoicePayPrototype, payrollFeature, isDashboardPageLive, isTreasuryLive, isCardsPageLive } = useFlags();

  if (isLoading) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data) {
    Sentry.addBreadcrumb({
      category: 'layout',
      message: 'Layout showing error boundary due to missing data',
      level: 'error',
      data: {
        hasError: !!error,
        errorMessage: error?.message || 'undefined',
        errorType: typeof error,
        isLoading,
        dataExists: !!data,
      },
    });

    showBoundary(error || new Error('Data is undefined in Layout component'));

    return null;
  }

  const { accounts } = data;

  const accountsWithInternalPaymentCapability = accounts.filter(canInitiateInternalPayments);
  const accountsWithExternalPaymentCapability = accounts.filter(canInitiateExternalPayments);

  // TODO: Add vendors back in https://linear.app/seapoint/issue/TENG-1760/re-add-vendors-page-when-ui-is-updated
  const baseNavItems: SideNavItem[] = ['TRANSACTIONS', 'ACCOUNTS'];

  if (isTreasuryLive) {
    baseNavItems.push('TREASURY');
  }

  baseNavItems.push('PAYMENTS');

  if (isCardsPageLive) {
    baseNavItems.push('CARDS');
  }

  baseNavItems.push('REPORTING');

  const navItems: SideNavItem[] = baseNavItems;
  if (invoicePayPrototype) {
    navItems.push('INVOICE_PAY');
  }
  if (payrollFeature) {
    navItems.push('PAYROLLS');
  }

  if (isDashboardPageLive) {
    navItems.unshift('DASHBOARD');
  }

  const disabledNavItems =
    [...accountsWithInternalPaymentCapability, ...accountsWithExternalPaymentCapability].length === 0
      ? [{ item: 'PAYMENTS' as const, label: 'You need a Seapoint account to make a payment' }]
      : [];

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex h-screen w-full">
        <SideNavNew
          items={navItems}
          disabledItems={disabledNavItems}
          userInfo={
            authenticatedInfo
              ? {
                  name: authenticatedInfo.name,
                  email: authenticatedInfo.email,
                  avatarUrl: authenticatedInfo.picture,
                }
              : undefined
          }
          baseCurrency={data.baseCurrency}
          onCurrencyChange={mutations.setBaseCurrency}
          onTeamClick={() => navigate('/team')}
          onSwitchOrgClick={() => {
            window.location.href = '/auth/login';
          }}
          onIntegrationsClick={() => navigate('/integrations')}
          onLogout={userMutations.logout}
        />

        {/* Main Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Main Content */}
          <main className="flex-1 overflow-y-auto bg-sidebar">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Layout;
